import React, { memo, useCallback, useMemo } from "react";
import { MultiValue, Select } from "chakra-react-select";
import { DistributionGroupFragment } from "gql/graphql";
import colors from "app/colors";
import { SystemStyleObject } from "@chakra-ui/react";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import DistributionGroupOptionLabel from "modules/selectors/distribution-group-option-label";

interface IProps {
  isDisabled?: boolean;
  onChange: (value: DistributionGroupFragment[]) => void;
  excludeDistributionGroupIds?: string[];
}

const AdminDistributionGroupSelect = ({
  isDisabled = false,
  onChange,
  excludeDistributionGroupIds,
}: IProps) => {
  const { distributionGroups } = useDistributionGroups();

  const options = useMemo(
    () =>
      distributionGroups.filter(
        (distributionGroup) =>
          !(excludeDistributionGroupIds ?? []).includes(distributionGroup.id),
      ),
    [excludeDistributionGroupIds, distributionGroups],
  );

  const _onChange = useCallback(
    (option: MultiValue<DistributionGroupFragment>) => {
      onChange([...option]);
    },
    [onChange],
  );

  const formatOptionLabel = useCallback(
    (option: DistributionGroupFragment) => (
      <DistributionGroupOptionLabel distributionGroup={option} search="" />
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (option: DistributionGroupFragment) => option.name,
    [],
  );

  const getOptionValue = useCallback(
    (option: DistributionGroupFragment) => option.id,
    [],
  );

  const isEmpty = options.length === 0;

  return (
    <Select
      options={options}
      isDisabled={isDisabled || isEmpty}
      isClearable
      isMulti
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={_onChange}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
        }),
        multiValue: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          borderRadius: "60px",
          borderWidth: "1px",
          borderColor: colors.blue,
          backgroundColor: colors.white,
        }),
        multiValueRemove: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          color: colors.blue,
        }),
      }}
      placeholder={
        isEmpty
          ? "No distribution groups available"
          : "Select a distribution group..."
      }
    />
  );
};

export default memo(AdminDistributionGroupSelect);
